import { calgaryDateString, calgaryCalendarDate, CALGARY_TIME_ZONE } from '../../../../utils/calgaryTime';
import React, { useState, useEffect, useCallback } from "react";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import FinancialService from "../../../../AppSettings/services/FinancialService";
import EmployeeService from "../../../../AppSettings/services/EmployeeService";
import { normalizeToArray } from "../../../../helpers/responseHelpers";
import { parseLocalDate } from "../../../../helpers/dateHelpers";
import "../../../../Components/Export/ExportModal.css";

export interface BillingExportItem {
  type: string;
  id: string;
  /** Billed-time primary key (bt_id); present for Billable Time rows, blank otherwise. */
  bt_id?: string;
  amount: number;
  description: string;
  date: string;
  dateObj: Date;
  employee: string;
  notes?: string;
  projectNumber: string;
  projectStatus: string;
  costCode: string;
  hoursQuantity: number;
  costRate: number;
  billRate: number;
  invoiceNumber: string;
}

interface EmployeeOption {
  id: string;
  name: string;
}

interface TimeCardDay {
  date: Date;
  dateStr: string;
  dayName: string;
  hours: number;
}

/* ── billing export constants ── */

const COLUMN_KEYS = [
  "bt_id",
  "projectNumber",
  "projectStatus",
  "type",
  "costCode",
  "employee",
  "hoursQuantity",
  "costRate",
  "billRate",
  "date",
  "invoiceNumber",
] as const;

/** Shown in exports when billed_time.matterid was cleared (e.g. engineering project delete). */
export const DELETED_PROJECT_LABEL = "Project was deleted";

const COLUMN_LABELS: Record<string, string> = {
  bt_id: "BT ID",
  projectNumber: "Project Number",
  projectStatus: "Project Status",
  type: "Type",
  costCode: "Cost Code",
  employee: "Employee/Item",
  hoursQuantity: "Hours/Quantity",
  costRate: "Cost Rate",
  billRate: "Bill Rate",
  date: "Date",
  invoiceNumber: "Invoice #",
};

function billingDataProcessor(
  data: BillingExportItem[] | null | undefined,
  selectedColumns: string[]
): string[][] {
  if (!data || !Array.isArray(data)) return [];
  if (selectedColumns.length === 0) return [];
  const headerRow = selectedColumns.map((col) => COLUMN_LABELS[col] ?? col);
  const dataRows = data.map((item) =>
    selectedColumns.map((col) => {
      const value = (item as unknown as Record<string, unknown>)[col];
      if (col === "projectNumber") {
        const projectNumber = value == null ? "" : String(value).trim();
        if (!projectNumber) return DELETED_PROJECT_LABEL;
        return projectNumber;
      }
      if (value === undefined || value === null) return "";
      return String(value);
    })
  );
  return [headerRow, ...dataRows];
}

/* ── helpers ── */

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatDateLong(d: Date): string {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatHours(h: number): string {
  if (h <= 0) return "0:00";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}:${String(mins).padStart(2, "0")}`;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseTimeRecordedToHours(timeRecorded: string | undefined | null): number {
  if (timeRecorded == null || String(timeRecorded).trim() === "") return 0;
  const s = String(timeRecorded).trim();
  if (s.includes(":")) {
    const parts = s.split(":");
    return (parseInt(parts[0], 10) || 0) + (parseInt(parts[1], 10) || 0) / 60 + (parseInt(parts[2], 10) || 0) / 3600;
  }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function eachDayOfRange(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/* ── props ── */

export interface BillingExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: BillingExportItem[];
  /** When true (permish 2/3), Time Card only — locked to currentEmployeeId. */
  ownTimeCardOnly?: boolean;
  currentEmployeeId?: string | null;
}

/* ── component ── */

const BillingExportModal: React.FC<BillingExportModalProps> = ({
  isOpen,
  onClose,
  data,
  ownTimeCardOnly = false,
  currentEmployeeId = null,
}) => {
  const [activeTab, setActiveTab] = useState<"billing" | "timecard">(
    ownTimeCardOnly ? "timecard" : "billing"
  );

  // billing tab state
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...COLUMN_KEYS]);
  const [isExporting, setIsExporting] = useState(false);

  // time card tab state
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    if (ownTimeCardOnly) {
      setActiveTab("timecard");
    }
  }, [isOpen, ownTimeCardOnly]);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoadingEmployees(true);
    EmployeeService.getEmployees()
      .then((emps) => {
        const options: EmployeeOption[] = (emps || []).map((e: any) => ({
          id: e.EmployeeID || e.employeeid || "",
          name: `${e.FirstName || e.firstname || ""} ${e.LastName || e.lastname || ""}`.trim() || e.EmployeeID || "",
        }));
        setEmployees(options);
        if (ownTimeCardOnly && currentEmployeeId) {
          setSelectedEmployeeIds([currentEmployeeId]);
        }
      })
      .catch(() => {
        setEmployees([]);
        if (ownTimeCardOnly && currentEmployeeId) {
          setSelectedEmployeeIds([currentEmployeeId]);
        }
      })
      .finally(() => setIsLoadingEmployees(false));
  }, [isOpen, ownTimeCardOnly, currentEmployeeId]);

  useEffect(() => {
    if (!isOpen) return;
    if (startDate && endDate) return;
    const now = calgaryCalendarDate();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDate(toISODate(firstDay));
    setEndDate(toISODate(now));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* ── billing export handlers ── */

  const handleColumnToggle = (col: string) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const toggleAllColumns = () => {
    setSelectedColumns((prev) =>
      prev.length === COLUMN_KEYS.length ? [] : [...COLUMN_KEYS]
    );
  };

  const exportBillingCSV = async () => {
    setIsExporting(true);
    try {
      const rows = billingDataProcessor(data, selectedColumns);
      const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `billing_export_${calgaryDateString()}.csv`);
      onClose();
    } catch (e) {
      console.error("CSV export error:", e);
    } finally {
      setIsExporting(false);
    }
  };

  // ExcelJS behind a lazy import (the Long Sheet precedent, plans/completed/timesheet-field-ticket-quality/frontend-xlsx-removal.md):
  // same rows, same auto-fit widths — only the serializer changed library.
  const exportBillingXLSX = async () => {
    setIsExporting(true);
    try {
      const rows = billingDataProcessor(data, selectedColumns);
      const { downloadAoaWorkbook } = await import("../../../../Components/Export/aoaWorkbook");
      await downloadAoaWorkbook(
        [{ name: "Billing", rows, widths: "auto" }],
        `billing_export_${calgaryDateString()}.xlsx`
      );
      onClose();
    } catch (e) {
      console.error("XLSX export error:", e);
    } finally {
      setIsExporting(false);
    }
  };

  /* ── employee selection ── */

  const handleEmployeeToggle = (id: string) => {
    if (ownTimeCardOnly) return;
    setSelectedEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const handleSelectAllEmployees = () => {
    if (ownTimeCardOnly) return;
    if (selectAll) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(employees.map((e) => e.id));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (ownTimeCardOnly) return;
    setSelectAll(
      employees.length > 0 && selectedEmployeeIds.length === employees.length
    );
  }, [selectedEmployeeIds, employees, ownTimeCardOnly]);

  /* ── time card data builder ── */

  const buildTimeCardData = useCallback(async () => {
    // Hard lock: own-time-card mode always uses currentEmployeeId only.
    const employeeIdsForExport =
      ownTimeCardOnly
        ? currentEmployeeId
          ? [currentEmployeeId]
          : []
        : selectedEmployeeIds;

    if (!startDate || !endDate || employeeIdsForExport.length === 0) return [];

    const rawBilledTime = await FinancialService.getAllBilledTime();
    const allEntries = normalizeToArray(rawBilledTime);

    const rangeStart = new Date(startDate + "T00:00:00");
    const rangeEnd = new Date(endDate + "T23:59:59");
    const days = eachDayOfRange(rangeStart, rangeEnd);

    const empMap = new Map(employees.map((e) => [e.id, e.name]));

    return employeeIdsForExport.map((empId) => {
      const empName = empMap.get(empId) || empId;
      const empEntries = allEntries.filter((entry: any) => {
        if ((entry.employeeid || "") !== empId) return false;
        const d = parseLocalDate(entry.date_recorded ?? entry.date);
        return d >= rangeStart && d <= rangeEnd;
      });

      const hoursByDate = new Map<string, number>();
      empEntries.forEach((entry: any) => {
        const d = entry.date_recorded ?? entry.date;
        const key = typeof d === "string" ? d.slice(0, 10) : toISODate(new Date(d));
        const hours = parseTimeRecordedToHours(entry.time_recorded);
        hoursByDate.set(key, (hoursByDate.get(key) || 0) + hours);
      });

      const dayRows: TimeCardDay[] = days
        .map((d) => {
          const key = toISODate(d);
          return {
            date: d,
            dateStr: formatDateShort(d),
            dayName: DAY_NAMES[d.getDay()],
            hours: hoursByDate.get(key) || 0,
          };
        })
        .filter((d) => d.hours > 0);

      const totalHours = dayRows.reduce((s, r) => s + r.hours, 0);

      return { empId, empName, dayRows, totalHours };
    });
  }, [startDate, endDate, selectedEmployeeIds, employees, ownTimeCardOnly, currentEmployeeId]);

  /* ── PDF generation ── */

  const generateTimeCardPDF = async () => {
    setIsGenerating(true);
    try {
      const cards = await buildTimeCardData();
      if (!cards || cards.length === 0) return;

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 16;
      const contentW = pageW - margin * 2;

      cards.forEach((card, cardIdx) => {
        if (cardIdx > 0) doc.addPage();

        let y = margin;

        doc.setFillColor(55, 65, 81);
        doc.rect(0, 0, pageW, 28, "F");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(200, 200, 200);
        doc.text(new Date().toLocaleString('en-CA', { timeZone: CALGARY_TIME_ZONE, timeZoneName: 'short' }), margin, 10);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text("Staff Time Card", pageW / 2, 10, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(200, 200, 200);
        doc.text("SEPT Software", pageW - margin, 10, { align: "right" });

        y = 38;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(30, 41, 59);
        doc.text(`${card.empName} Time Card`, margin, y);

        y += 7;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        const rangeLabel = `${formatDateLong(new Date(startDate + "T00:00:00"))} - ${formatDateLong(new Date(endDate + "T00:00:00"))}`;
        doc.text(rangeLabel, margin, y);

        y += 10;
        doc.setFontSize(8.5);
        doc.setTextColor(100, 116, 139);
        doc.text(`Staff: ${card.empName}`, margin, y);
        doc.text("Time-track status: All time entries", margin + contentW * 0.5, y);

        y += 8;
        const colDays = contentW * 0.6;
        const colSpent = contentW * 0.2;
        const rowH = 7;

        doc.setFillColor(55, 65, 81);
        doc.rect(margin, y, contentW, rowH + 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(255, 255, 255);
        doc.text("Days", margin + 3, y + 5);
        doc.text("Spent Time", margin + colDays + 3, y + 5);
        doc.text(card.empName.split(" ")[0] || "Employee", margin + colDays + colSpent + 3, y + 5);
        y += rowH + 1;

        doc.setFillColor(245, 247, 250);
        doc.rect(margin, y, contentW, rowH, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(30, 41, 59);
        doc.text("Total Working Time:", margin + colDays - 55, y + 5);
        const totalStr = formatHours(card.totalHours);
        if (card.totalHours > 0) {
          const tw = doc.getTextWidth(totalStr);
          doc.setFillColor(234, 179, 8);
          doc.rect(margin + colDays + 3 - 1, y + 0.5, tw + 3, rowH - 1, "F");
          doc.setTextColor(30, 41, 59);
        }
        doc.text(totalStr, margin + colDays + 3, y + 5);
        doc.text(totalStr, margin + colDays + colSpent + 3, y + 5);
        y += rowH;

        card.dayRows.forEach((day, i) => {
          if (y + rowH > pageH - margin) {
            doc.addPage();
            y = margin;
          }

          if (i % 2 === 0) {
            doc.setFillColor(250, 250, 252);
            doc.rect(margin, y, contentW, rowH, "F");
          }

          doc.setDrawColor(229, 231, 235);
          doc.line(margin, y + rowH, margin + contentW, y + rowH);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.5);
          doc.setTextColor(55, 65, 81);
          doc.text(`${day.dateStr}, ${day.dayName}`, margin + 3, y + 5);

          const hStr = formatHours(day.hours);
          doc.setTextColor(55, 65, 81);
          doc.text(hStr, margin + colDays + 3, y + 5);
          doc.text(hStr, margin + colDays + colSpent + 3, y + 5);

          y += rowH;
        });

        doc.setDrawColor(55, 65, 81);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + contentW, y);
      });

      doc.save(`time_cards_${calgaryDateString()}.pdf`);
      onClose();
    } catch (e) {
      console.error("PDF generation error:", e);
    } finally {
      setIsGenerating(false);
    }
  };

  /* ── Excel time card generation ── */

  const generateTimeCardExcel = async () => {
    setIsGenerating(true);
    try {
      const cards = await buildTimeCardData();
      if (!cards || cards.length === 0) return;

      // One sheet per employee, exactly as before — the rows build here, the ExcelJS
      // serializer loads lazily (plans/completed/timesheet-field-ticket-quality/frontend-xlsx-removal.md).
      const sheets = cards.map((card) => {
        const rows: (string | number)[][] = [];

        rows.push(["Staff Time Card"]);
        rows.push([`Employee: ${card.empName}`]);
        const rangeLabel = `${formatDateLong(new Date(startDate + "T00:00:00"))} - ${formatDateLong(new Date(endDate + "T00:00:00"))}`;
        rows.push([`Period: ${rangeLabel}`]);
        rows.push([]);
        rows.push(["Date", "Day", "Spent Time", "Hours (decimal)"]);
        rows.push(["TOTAL", "", formatHours(card.totalHours), Math.round(card.totalHours * 100) / 100]);

        card.dayRows.forEach((day) => {
          rows.push([
            day.dateStr,
            day.dayName,
            day.hours > 0 ? formatHours(day.hours) : "",
            day.hours > 0 ? Math.round(day.hours * 100) / 100 : "",
          ]);
        });

        return { name: card.empName.slice(0, 31), rows, widths: [22, 14, 14, 16] };
      });

      const { downloadAoaWorkbook } = await import("../../../../Components/Export/aoaWorkbook");
      await downloadAoaWorkbook(sheets, `time_cards_${calgaryDateString()}.xlsx`);
      onClose();
    } catch (e) {
      console.error("Excel time card error:", e);
    } finally {
      setIsGenerating(false);
    }
  };

  const timeCardReady =
    !!startDate &&
    !!endDate &&
    startDate <= endDate &&
    (ownTimeCardOnly ? !!currentEmployeeId : selectedEmployeeIds.length > 0);

  const ownEmployeeName =
    (currentEmployeeId && employees.find((e) => e.id === currentEmployeeId)?.name) ||
    "Your time card";

  if (!isOpen) return null;

  const busy = isExporting || isGenerating;

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="export-modal-header">
          <h3>{ownTimeCardOnly ? "Export Time Card" : "Export"}</h3>
          <button className="export-modal-close" onClick={onClose} disabled={busy}>
            &times;
          </button>
        </div>

        {/* tabs — hidden in own-time-card mode */}
        {!ownTimeCardOnly && (
          <div className="export-report-type-section" style={{ margin: 0, padding: 0, borderBottom: "none" }}>
            <div className="export-report-type-options" style={{ gap: 0, justifyContent: "stretch" }}>
              <label
                className="export-report-type-option"
                style={{
                  flex: 1,
                  justifyContent: "center",
                  borderRadius: 0,
                  borderBottom: activeTab === "billing" ? "2px solid #667eea" : "2px solid transparent",
                  background: "transparent",
                }}
                onClick={() => setActiveTab("billing")}
              >
                <span
                  className={`export-report-type-label${activeTab === "billing" ? " export-tab-active" : ""}`}
                >
                  Billing Data
                </span>
              </label>
              <label
                className="export-report-type-option"
                style={{
                  flex: 1,
                  justifyContent: "center",
                  borderRadius: 0,
                  borderBottom: activeTab === "timecard" ? "2px solid #667eea" : "2px solid transparent",
                  background: "transparent",
                }}
                onClick={() => setActiveTab("timecard")}
              >
                <span
                  className={`export-report-type-label${activeTab === "timecard" ? " export-tab-active" : ""}`}
                >
                  Time Card
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="export-modal-body">
          {/* ── BILLING TAB ── */}
          {!ownTimeCardOnly && activeTab === "billing" && (
            <>
              <div className="export-column-section">
                <div className="export-section-header">
                  <h4>Select Columns to Export</h4>
                  <button
                    className="export-toggle-all-btn"
                    onClick={toggleAllColumns}
                    disabled={busy}
                  >
                    {selectedColumns.length === COLUMN_KEYS.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="export-columns-grid">
                  {COLUMN_KEYS.map((col) => (
                    <label key={col} className="export-column-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col)}
                        onChange={() => handleColumnToggle(col)}
                        disabled={busy}
                      />
                      <span className="export-column-label">{COLUMN_LABELS[col]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="export-format-section">
                <h4>Choose Export Format</h4>
                <div className="export-format-options">
                  <button
                    className="export-format-btn csv-btn"
                    onClick={exportBillingCSV}
                    disabled={busy || selectedColumns.length === 0}
                  >
                    <span className="export-icon">📊</span>
                    <div className="export-format-info">
                      <div className="export-format-title">CSV</div>
                      <div className="export-format-desc">Comma-separated values for data analysis</div>
                    </div>
                  </button>
                  <button
                    className="export-format-btn xlsx-btn"
                    onClick={exportBillingXLSX}
                    disabled={busy || selectedColumns.length === 0}
                  >
                    <span className="export-icon">📈</span>
                    <div className="export-format-info">
                      <div className="export-format-title">XLSX</div>
                      <div className="export-format-desc">Excel format with formatting and styling</div>
                    </div>
                  </button>
                </div>
              </div>

              {selectedColumns.length === 0 && (
                <div className="export-warning">
                  <span>Please select at least one column to export</span>
                </div>
              )}
            </>
          )}

          {/* ── TIME CARD TAB ── */}
          {(ownTimeCardOnly || activeTab === "timecard") && (
            <>
              {/* date range */}
              <div className="export-column-section">
                <div className="export-section-header">
                  <h4>Date Range</h4>
                </div>
                <div
                  className="export-columns-grid"
                  style={{ gridTemplateColumns: "1fr 1fr", gap: "1rem" }}
                >
                  <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span className="export-column-label">From</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={busy}
                      style={{
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #718096",
                        background: "#2d3748",
                        color: "#e2e8f0",
                        fontSize: "0.9rem",
                      }}
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span className="export-column-label">To</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={busy}
                      style={{
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #718096",
                        background: "#2d3748",
                        color: "#e2e8f0",
                        fontSize: "0.9rem",
                      }}
                    />
                  </label>
                </div>
                {startDate && endDate && startDate > endDate && (
                  <div className="export-warning" style={{ marginTop: "0.75rem" }}>
                    <span>Start date must be before end date.</span>
                  </div>
                )}
              </div>

              {/* employee selection — locked to self in own-time-card mode */}
              <div className="export-column-section">
                <div className="export-section-header">
                  <h4>{ownTimeCardOnly ? "Employee" : "Select Employees"}</h4>
                  {!ownTimeCardOnly && (
                    <button
                      className="export-toggle-all-btn"
                      onClick={handleSelectAllEmployees}
                      disabled={busy || employees.length === 0}
                    >
                      {selectAll ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>
                {ownTimeCardOnly ? (
                  <p
                    className="export-column-label"
                    style={{ margin: 0, fontSize: "0.95rem", color: "#e2e8f0" }}
                    data-testid="own-timecard-employee"
                  >
                    {isLoadingEmployees ? "Loading..." : ownEmployeeName}
                  </p>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Search employees..."
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      disabled={busy}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #718096",
                        background: "#2d3748",
                        color: "#e2e8f0",
                        fontSize: "0.9rem",
                        marginBottom: "0.75rem",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {isLoadingEmployees ? (
                      <div className="export-status">
                        <div className="export-loading">
                          <div className="export-spinner" />
                          <span>Loading employees...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="export-columns-grid">
                        {employees
                          .filter((emp) =>
                            emp.name.toLowerCase().includes(employeeSearch.toLowerCase())
                          )
                          .map((emp) => (
                            <label key={emp.id} className="export-column-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedEmployeeIds.includes(emp.id)}
                                onChange={() => handleEmployeeToggle(emp.id)}
                                disabled={busy}
                              />
                              <span className="export-column-label">{emp.name}</span>
                            </label>
                          ))}
                      </div>
                    )}
                    {selectedEmployeeIds.length > 0 && (
                      <p style={{ margin: "6px 0 0", fontSize: "0.85rem", color: "#667eea", fontWeight: 500 }}>
                        {selectedEmployeeIds.length} employee{selectedEmployeeIds.length > 1 ? "s" : ""} selected
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* export buttons */}
              <div className="export-format-section">
                <h4>Generate Time Card</h4>
                <div className="export-format-options">
                  <button
                    className="export-format-btn csv-btn"
                    onClick={generateTimeCardPDF}
                    disabled={busy || !timeCardReady}
                  >
                    <span className="export-icon">📄</span>
                    <div className="export-format-info">
                      <div className="export-format-title">PDF</div>
                      <div className="export-format-desc">Printable time card report</div>
                    </div>
                  </button>
                  <button
                    className="export-format-btn xlsx-btn"
                    onClick={generateTimeCardExcel}
                    disabled={busy || !timeCardReady}
                  >
                    <span className="export-icon">📈</span>
                    <div className="export-format-info">
                      <div className="export-format-title">XLSX</div>
                      <div className="export-format-desc">Excel spreadsheet per employee</div>
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}

          {/* status */}
          {busy && (
            <div className="export-status">
              <div className="export-loading">
                <div className="export-spinner" />
                <span>{isGenerating ? "Generating time cards..." : "Exporting..."}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingExportModal;
